export interface LinearScale {
  map(value: number): number
  invert(pixel: number): number
  ticks(count?: number): number[]
}

export function linearScale(
  domain: [number, number],
  range: [number, number],
): LinearScale {
  const [domainMin, rawDomainMax] = domain
  const domainMax = rawDomainMax === domainMin ? domainMin + 1 : rawDomainMax
  const [rangeMin, rangeMax] = range
  const domainSpan = domainMax - domainMin
  const rangeSpan = rangeMax - rangeMin

  return {
    map(value) {
      return rangeMin + ((value - domainMin) / domainSpan) * rangeSpan
    },
    invert(pixel) {
      return domainMin + ((pixel - rangeMin) / rangeSpan) * domainSpan
    },
    ticks(count = 5) {
      const step = niceStep(domainSpan / Math.max(1, count))
      const first = Math.ceil(domainMin / step) * step
      const values: number[] = []
      for (let value = first; value <= domainMax + Number.EPSILON; value += step) {
        values.push(Number(value.toPrecision(12)))
      }
      return values
    },
  }
}

export function niceStep(rawStep: number): number {
  if (!Number.isFinite(rawStep) || rawStep <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(rawStep))
  const normalized = rawStep / magnitude
  const factor = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10
  return factor * magnitude
}

// Loop-based min/max: spreading long series into Math.min/max can overflow the call stack.
export function extent(values: Iterable<number>): [number, number] | null {
  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY
  for (const value of values) {
    if (!Number.isFinite(value)) continue
    if (value < min) min = value
    if (value > max) max = value
  }
  return min <= max ? [min, max] : null
}
